import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, forkJoin } from 'rxjs';
import { HttpError } from 'src/app/core/http-error';
import { BoxAccessibilityChangeEvent } from 'src/app/models/box-accessibility-change-event';
import { Rights } from 'src/app/models/enums/rights';
import { IParamsValues } from 'src/app/models/interfaces/params-values.interface';
import { ParamsValues } from 'src/app/models/params-values';
import { ViewAccessibilityChangeEvent } from 'src/app/models/view-accessibility-change-event';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { FormMode } from '../../controls/common/form-mode';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { IUser } from '../../models/interfaces/user.interface';
import { User } from '../../models/user';
import { UsersService } from '../../services/users.service';
import { GroupsTreeComponent } from '../groups/groups-tree/groups-tree.component';
import { UserDetailContext } from './user-detail-context';
import { UserDetailComponent } from './user-detail/user-detail.component';
import { UsersListComponent } from './users-list/users-list.component';

enum DisplayMode {
    List = 1,
    Tree = 2,
    Form = 4,
}

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, UsersListComponent, GroupsTreeComponent, DetailHeaderComponent, UserDetailComponent],
})
export class UsersComponent implements OnInit {
    /**
     * Reference au composant UsersList.
     */
    @ViewChild(UsersListComponent) usersList: UsersListComponent;

    /**
     * Reference au composant GroupsTree.
     */
    @ViewChild(GroupsTreeComponent) groupsTree: GroupsTreeComponent;

    /**
     * Liste des icônes utilisés dans le template
     */
    public faUser = faUser;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    public context: UserDetailContext = null;

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'édition doit apparaitre.
     */
    public displayEditbuttom = false;

    /**
     * Constructeur
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param usersService : Service métier utilisé pour gérer les utilisateurs.
     * @param paramsValuesService : Service métier utilisé pour gérer les paramètres.
     * @param memoryService : Service utilisé pour gérer le contexte courant.
     */
    constructor(
        protected notificationService: NotificationService,
        private usersService: UsersService,
        private paramsValuesService: ParamsValuesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.GroupAndUserEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un utilisateur.
     * @param user Utilisateur selectionné.
     */
    public onUserSelected(user: User) {
        this.reloadForm(user, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param user Utilisateur sélectionné.
     */
    public onUserDisplayed(user: User) {
        this.reloadForm(user, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param user Utilisateur sélectionné.
     */
    public onUserEdited(user: User) {
        this.reloadForm(user, FormMode.Edition);
    }

    /**
     * Appelé lorsque les données de l'utilisateur ont été mise à jour.
     * Il s'agit des modifications apportées à l'onglet Infos.
     * @param group : Données de l'utilisateur à modifier.
     */
    public userUpdated(user: User): void {
        this.spinner.show('main-spinner');
        if (this.context.editedUserId) {
            // Cas de la mise à jour.
            this.usersService.update(this.context.editedUserId, user).subscribe(
                (x) => {
                    this.refreshListOrTree(x.id);
                    this.displaySuccessNotification('Utilisateur mis à jour');
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour l'utilisateur." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appelé lorsque les paramètres ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Paramètres.
     * @param paramsValues : Paramètres à mettre à jour.
     */
    public paramsValuesUpdated(paramsValues: ParamsValues): void {
        this.spinner.show('main-spinner');
        if (paramsValues.id) {
            this.paramsValuesService.update(this.memoryService.getSelectedOrganization().id, paramsValues.id, paramsValues).subscribe(
                (x) => {
                    this.context.paramsContext.paramsValues = x;
                    this.displaySuccessNotification("Paramètres de l'utilisateur mis à jour");
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour le paramétrage de l'utilisateur." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            this.paramsValuesService.createForUser(this.context.userContext.item.id, paramsValues).subscribe(
                (x) => {
                    this.context.paramsContext.paramsValues = x;
                    this.displaySuccessNotification("Paramètres de l'utilisateur créé");
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer le paramétrage de l'utilisateur." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appelé lorsque les accès aux bannettes ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Accessibilité.
     * @param boxesAccessibility : Accès aux bannettes à mettre à jour.
     */
    public onAccessibilitiesUpdated(event: BoxAccessibilityChangeEvent): void {
        if (event) {
            this.spinner.show('main-spinner');
            this.usersService.updateBoxes(event.userId, event.boxAccessibilities).subscribe(
                (x) => {
                    this.displaySuccessNotification("Bannettes de l'utilisateur mises à jour");
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour les bannettes de l'utilisateur." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appelé lorsque les accès aux vues ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet "A"ccessibilité vues".
     * @param ViewAccessibilityChangeEvent : Evènement d'accès aux vues à mettre à jour.
     */
    public onViewsAccessibilitiesUpdated(event: ViewAccessibilityChangeEvent): void {
        if (event) {
            this.spinner.show('main-spinner');
            this.usersService.updateViews(event.userId, event.viewsAccessibilities).subscribe({
                next: (x) => {
                    this.displaySuccessNotification("Vues de l'utilisateur mises à jour");
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour les vues de l'utilisateur." + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        }
    }

    /**
     * Appellé lorsque l'utilisateur confirme la réinitialisation d'un utilisateur.
     * @param user Utilisateur sélectionné.
     */
    public onUserReseted(user: User) {
        this.spinner.show('main-spinner');
        // Réinitialisation effective de l'utilisateur.
        this.usersService.reset(user.id).subscribe(
            (x) => {
                this.refreshListOrTree(user.id);
                this.displaySuccessNotification('Utilisateur réinitialisé');
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                console.error(error);
                this.notificationService.displayError('Erreur', "Impossible de réinitialiser l'utilisateur." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton d'export.
     */
    public onUsersExported() {
        this.usersList.export('Utilisateurs.xlsx');
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton d'aide.
     */
    public onUsersHelped() {
        this.usersList.displayHelpDialog('users');
    }

    /**
     * Retourne une valeur indiquant si le formulaire est affiché (visible)
     * @returns True si le formulaire est visible.
     */
    public isFormDisplayed(): boolean {
        return DisplayMode.Form === (this.displayMode & DisplayMode.Form);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des groupes sous la forme d'une arborescence est actif.
     * L'affichage des groupes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des groupes sous la forme d'une arborescence est actif.
     */
    public isTreeDisplayed(): boolean {
        return DisplayMode.Tree === (this.displayMode & DisplayMode.Tree);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des groupes sous la forme d'une liste est actif.
     * L'affichage des groupes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des groupes sous la forme d'une liste est actif.
     */
    public isListDisplayed(): boolean {
        return DisplayMode.List === (this.displayMode & DisplayMode.List);
    }

    /**
     * Cache le formulaire.
     * La liste ou l'arboresence est alors visible.
     */
    public hideForm(): void {
        this.displayMode &= ~DisplayMode.Form;
    }

    /**
     * Définit le mode d'affichage de la page (Form / List / Tree).
     * Les modes List et Tree sont exclusifs. Par contre le mode Form peut être associés à l'un des 2 autres.
     * Exemple : si le mode est à Tree, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera l'arborescence.
     * si le mode est à List, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera la liste.
     * @param mode Mode d'affichage
     */
    public setDisplayMode(mode: DisplayMode) {
        if (mode == DisplayMode.Form) {
            this.displayMode |= mode;
        } else {
            this.displayMode = mode;
        }
    }

    /**
     * Charge les informations d'un utilisateur.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param userId : Id de l'utilisateur à charger.
     * @returns Les informations de l'utilisateur.
     */
    private loadUser(userId: string): Observable<User> {
        return this.usersService.get(userId);
    }

    /**
     * Charge le paramétrage d'un utilisateur.
     * Il s'agit des données affichées dans l'onglet Paramètres du détail.
     * @param userId : Id de l'utilisateur à charger.
     * @returns Les paramètres de l'utilisateur.
     */
    private loadParams(userId: string): Observable<ParamsValues> {
        return this.paramsValuesService.getForUser(userId);
    }

    /**
     * Charge le paramétrage hérité par l'utilisateur.
     * Il s'agit des données affichées dans l'onglet Paramètres du détail.
     * @param userId : Id de l'utilisateur à charger.
     * @returns Les paramètres hérités par l'utilisateur.
     */
    private loadInheritedParamsValues(userId: string): Observable<ParamsValues> {
        return this.paramsValuesService.getInheritedByUser(userId);
    }

    /**
     * Affiche Les données d'un utilisateur dans le formulaire.
     * @param group : Instance de l'utilisateur à afficher dans le formulaire.
     * @param paramsValues Paramétrage de l'utilisateur (Il s'agit des valeurs définies explicitement pour cet utilisateur sans héritage)
     * @param inheritedParamsValues Paramétrage hérités (Il s'agit des valeurs héritées des groupes parents et de l'oganisation).
     * @param formMode Mode d'affichage du formulaire.
     */
    private displayForm(user: IUser, paramsValues: IParamsValues, inheritedParamsValues: IParamsValues, formMode: FormMode): void {
        this.createContext(user, paramsValues, inheritedParamsValues, formMode);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Recharge le formulaire pour l'utilisateur spécifié
     * @param group Instance de l'utilisateur à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    private reloadForm(user: IUser, formMode: FormMode): void {
        this.spinner.show('main-spinner');
        const user$ = this.loadUser(user.id);
        const params$ = this.loadParams(user.id);
        const inheritedParams$ = this.loadInheritedParamsValues(user.id);
        // TODO : charger les params parents que ce soit du groupe ou de l'organisation.
        forkJoin([user$, params$, inheritedParams$]).subscribe(
            (x) => {
                this.displayForm(x[0], x[1], x[2], formMode);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'utilisateur." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Raffraichit le contenu de la liste ou de l'arborescence.
     * @param userId Id de l'utilisateur dont l'affichage doit être raffraichi.
     */
    private refreshListOrTree(userId: string) {
        if (this.isListDisplayed()) {
            this.usersList.refresh();
        } else {
            this.groupsTree.refreshUser(userId);
        }
    }

    /**
     * Affiche une notification en cas de succès d'une action serveur.
     * @param notificationText: Texte de la notification.
     */
    private displaySuccessNotification(notificationText: string): void {
        this.notificationService.displaySuccess(null, notificationText);
    }

    /**
     * Crée un contexte utilisé pour l'affichage du formulaire.
     * @param group : Instance de l'utilisateur à afficher dans le formulaire.
     * @param paramsValues Paramétrage de l'utilisateur (Il s'agit des valeurs définies explicitement pour cet utilisateur sans héritage)
     * @param inheritedParamsValues Paramétrage hérités (Il s'agit des valeurs héritées des groupes parents et de l'oganisation).
     * @param formMode Mode d'affichage du formulaire.
     * @returns Le contexte utilisé pour l'affichage du formulaire.
     */
    private createContext(user: IUser, paramsValues: IParamsValues, inheritedParamsValues: IParamsValues, formMode: FormMode): void {
        let context = new UserDetailContext();
        context.userContext.item = user;
        context.editedUserId = user.id;
        context.paramsContext.paramsValues = paramsValues;
        context.paramsContext.groupParentParamsValues = inheritedParamsValues;
        context.formMode = formMode;

        this.context = context;
    }
}
